import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';

export default class MQTTSubNode extends BaseNode {
    constructor() {
        super();
        this.name = 'On MQTT Notification';
        this.id = 'node_mqtt_sub';
        this.icon = 'AiFillNotification';
        this.linkedFeature = 'mqtt';
        this.requirements = ['network'];

        const settings: Setting[] = [
            {
                id: 'TOPIC',
                name: 'Topic',
                settingType: SettingType.OPTION,
                required: true,
                refId: 'mqtt_topic',
                default: '',
                options: [],
            },
        ];

        this.settings = this.settings.concat(settings);

        this.outputs = [
            {
                name: 'MQTT Notification',
                portId: 'callback_mqtt_notif',
                params: [
                    {
                        name: 'data',
                        dataType: TibboDataType.STRING,
                        default: '',
                    },
                    {
                        name: 'topic',
                        dataType: TibboDataType.STRING,
                        default: '',
                    },
                ],
            },
        ];
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const project: ProjectConfiguration = this.project;
            const TOPIC = project.getFeature(this.getSettingValue('TOPIC'));
            if (TOPIC) {
                const topicDescription = TOPIC.getSettingValue('NAME');
                let topicName;
                for (let i = 0; i < TOPIC.settings.length; i++) {
                    const topicSetting = TOPIC.settings[i];
                    if (topicSetting.id === 'NAME') {
                        topicName = topicSetting.value.toString();
                    }
                }
                return `MQTT {{$${topicName}}} Notification`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const TOPIC = project.getFeature(this.getSettingValue('TOPIC'));
        if (TOPIC) {
            const topicDescription = TOPIC.getSettingValue('NAME');
            let topicName;
            for (let i = 0; i < TOPIC.settings.length; i++) {
                const topicSetting = TOPIC.settings[i];
                if (topicSetting.id === 'TOPIC_NAME') {
                    topicName = topicSetting.value.toString();
                }
            }
            return `MQTT Notification {{$${topicName}}} (${topicDescription})`;
        }
        return this.name;
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;
        const topicFeature = project.getFeature(this.getSettingValue('TOPIC'));
        const codeLocationStart = PlatformUtils.getRuntimeCodeLocationStart(project.runtime);
        const codeLocationEnd = PlatformUtils.getRuntimeCodeLocationEnd(project.runtime);
        if (!topicFeature) {
            return blocks;
        }

        const links = project.getNodeLinks(this, 'callback_mqtt_notif');
        for (let i = 0; i < links.length; i += 1) {
            const link = links[i];
            blocks.push({
                location: `mqtt_notif_${topicFeature.getSettingValue('TOPIC_NAME', true)}`,
                blockType: BlockType.CODE,
                template: `${codeLocationStart}${link.getSourceId()}${codeLocationEnd}`,
            });
        }

        return blocks;
    }

    getDetails(): string {
        return `
This block is triggered when the device receives an MQTT notification.`;
    }
}
